/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getFirstFocusable, getId, IsFocusVisibleClassName } from '@fluentui/react/lib/Utilities';
import { AnimationClassNames } from '@fluentui/style-utilities';
import * as React from 'react';
import { useMountLogger } from 'src/core/hooks/useMountLogger';
import { useZoomLevel } from 'src/core/hooks/useZoomLevel';
import { getStylistV2, joinClassNames } from 'src/core/themes/luisStylist';
import { noopUndefined } from 'src/core/utils/base';

export type PageProps = {
    /**
     * Controls the styling to render the page with. Corresponds
     * to the bootstrap jargon of containers. Pages are meant to
     * take 100% height of their container, and are meant to be
     * vertically scrollable.
     *
     * - container: Centers content in the middle of the page
     * with large margins on the left and right of it. Suitable
     * for one column pages like application list pages, user
     * settings page, etc.
     *
     * - container-fluid: Takes the full width of the page but
     * adds some padding around the div.
     *
     * - undefined (default): Takes the full width of the page
     * with no padding or margining.
     */
    size?: 'container' | 'container-fluid';

    /**
     * The title to appear in the tab of the browser.
     */
    title?: string;

    /**
     * The page name to use for logging.
     */
    pageName: string;

    /**
     * A flag to whether fade in when loading.
     */
    animateIn?: boolean;

    /**
     * An additional class name for extensible styling.
     */
    className?: string;

    /**
     * Whether to change page height based on page zoom
     * or not. If true, it ensures the entire page overflows
     * when the zoom level is above a certain percentage.
     * @default true
     */
    reactToZoomLevel?: boolean;

    /**
     * event occurs when page’s scrollbar is being scrolled
     * @default noopUndefined
     */
    onScroll?: (e: any) => void;
};

export const focusCaptureId = getId();

const { styleDiv } = getStylistV2('Page');

const PageRootContainer = styleDiv(
    'PageRootContainer',
    (_, cssVars) => ({
        width: '100%',
        height: '100%',
        minWidth: '60%',
        overflowY: 'auto',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        padding: cssVars.padding
    }),
    {
        cssVars: { padding: '' },
        cssVarsSetter: ({ size }: { size: PageProps['size'] }, theme) => {
            switch (size) {
                case 'container':
                    return { padding: theme.containerPadding };
                case 'container-fluid':
                    return { padding: theme.containerFluidPadding };
                default:
                    return { padding: '' };
            }
        }
    }
);

const FocusCaptureContainer = styleDiv(
    'FocusCaptureContainer',
    (_, cssVars) => ({ display: 'flex', flexDirection: 'column', flex: 1, height: cssVars.height }),
    {
        cssVars: { height: '' },
        cssVarsSetter: ({ isFullHeight }: { isFullHeight: boolean }) => ({ height: isFullHeight ? '100%' : 'unset' })
    }
);

/**
 * A unified page component that wraps children into a flex
 * display, and adds padding based on page size.
 *
 * @param props The input to the page.
 */
export const Page = (props: React.PropsWithChildren<PageProps>) => {
    const {
        onScroll = noopUndefined,
        title,
        pageName,
        size,
        animateIn = true,
        reactToZoomLevel = true,
        className,
        children,
        ...rest
    } = props;

    const { isAccessibleMode } = useZoomLevel();
    useMountLogger({ activitySource: pageName });
    const focusCaptureContainerRef = React.useRef<HTMLDivElement>(null);

    const classNameToApply = joinClassNames(className, animateIn ? AnimationClassNames.fadeIn200 : '');

    React.useEffect(() => {
        if (title) {
            document.title = title;
        }

        /**
         * Focuses the first element of the page automatically only if the user was using the keyboard.
         * This check is done using the Office Fabric focus class that is automatically placed on the
         * body element whenever the user uses the keyboard and removed when the user uses the mouse.
         */
        if (focusCaptureContainerRef.current && document.body.className.indexOf(IsFocusVisibleClassName) !== -1) {
            getFirstFocusable(focusCaptureContainerRef.current, focusCaptureContainerRef.current, true)?.focus();
        }
    }, [title]);

    return (
        <PageRootContainer onScroll={onScroll} role="main" customProps={{ size }} className={classNameToApply} {...rest}>
            <FocusCaptureContainer
                id={focusCaptureId}
                originalRef={focusCaptureContainerRef}
                customProps={{ isFullHeight: reactToZoomLevel ? !isAccessibleMode : true }}>
                {children}
            </FocusCaptureContainer>
        </PageRootContainer>
    );
};
